import { distanceKilometres, type LocationPoint, type Venue } from '../planning.ts'
import { roadMatrix } from '../road/client.ts'
import type { StationAccess, TransitResult } from '../transit/router.ts'
import type { MultimodalNode } from './data.ts'
import type { ScheduledModeSet } from './protocol.ts'

const BUS_BOARDING_SECONDS = 60
const FERRY_BOARDING_SECONDS = 10 * 60
const BICYCLE_PARKING_SECONDS = 3 * 60
const BIKE_SHARE_ESTIMATED_OVERHEAD_SECONDS = 6 * 60
const MAXIMUM_BICYCLE_ACCESS_KM = 8
const MAXIMUM_BICYCLE_DESTINATIONS = 25

type MatrixLoader = typeof roadMatrix

export function multimodalAccessPoints(
  nodes: MultimodalNode[],
  origin: Pick<LocationPoint, 'lat' | 'lon'>,
  allowedModes: ScheduledModeSet,
): StationAccess[] {
  return [
    ...(allowedModes.includes('subway') ? accessForMode(nodes, origin, 'subway', 1.2, 5, 0) : []),
    ...(allowedModes.includes('bus') ? accessForMode(nodes, origin, 'bus', 0.8, 12, BUS_BOARDING_SECONDS) : []),
    ...(allowedModes.includes('ferry') ? accessForMode(nodes, origin, 'ferry', 1.2, 8, FERRY_BOARDING_SECONDS) : []),
  ]
}

export function estimatedBicycleTransitAccessPoints(
  nodes: MultimodalNode[],
  origins: LocationPoint[],
  allowedModesByOrigin: ScheduledModeSet[],
  bicycleEnabledByOrigin: boolean[],
): StationAccess[][] {
  return origins.map((origin, originIndex) => bicycleEnabledByOrigin[originIndex]
    ? allowedModesByOrigin[originIndex].flatMap((mode) => nearestNodes(nodes, origin, mode, 25).map((stationIndex) => {
      const node = nodes[stationIndex]
      const travelSeconds = estimatedBicycleSeconds(origin, node)
      return {
        stationIndex,
        mode: 'bike' as const,
        travelSeconds,
        seconds: travelSeconds + BICYCLE_PARKING_SECONDS + boardingSeconds(mode),
      }
    }))
    : [])
}

export function estimatedBikeShareTransitAccessPoints(
  nodes: MultimodalNode[],
  origins: LocationPoint[],
  allowedModesByOrigin: ScheduledModeSet[],
  bikeShareEnabledByOrigin: boolean[],
): StationAccess[][] {
  return origins.map((origin, originIndex) => bikeShareEnabledByOrigin[originIndex]
    ? allowedModesByOrigin[originIndex].flatMap((mode) => nearestNodes(nodes, origin, mode, 25).map((stationIndex) => {
      const node = nodes[stationIndex]
      const travelSeconds = estimatedBicycleSeconds(origin, node)
      return {
        stationIndex,
        mode: 'bike-share' as const,
        travelSeconds,
        seconds: travelSeconds + BIKE_SHARE_ESTIMATED_OVERHEAD_SECONDS + boardingSeconds(mode),
      }
    }))
    : [])
}

export function prioritizedTransitAccessStations(
  nodes: MultimodalNode[],
  estimatedAccessByOrigin: StationAccess[][],
  preliminaryResults: TransitResult[],
): number[] {
  const useCounts = preliminaryResults.map((result) => {
    const counts = new Map<number, number>()
    result.journeys.forEach((journey) => {
      const access = journey?.legs[0]
      const stationIndex = journey?.nonWalkingAccessStation
        ?? (access?.kind === 'access' && access.mode !== undefined && access.mode !== 'walk'
          ? access.toStation : undefined)
      if (stationIndex !== undefined) {
        counts.set(stationIndex, (counts.get(stationIndex) ?? 0) + 1)
      }
    })
    return counts
  })
  const streams = estimatedAccessByOrigin.flatMap((access, originIndex) => {
    const candidates = [...access].sort((left, right) => (useCounts[originIndex].get(right.stationIndex) ?? 0)
      - (useCounts[originIndex].get(left.stationIndex) ?? 0)
      || left.seconds - right.seconds
      || nodes[left.stationIndex][4].localeCompare(nodes[right.stationIndex][4])
      || left.stationIndex - right.stationIndex)
    return candidates.length ? [{ cursor: 0, indexes: candidates.map(({ stationIndex }) => stationIndex) }] : []
  }).sort((left, right) => {
    const length = Math.min(left.indexes.length, right.indexes.length)
    for (let index = 0; index < length; index += 1) {
      if (left.indexes[index] !== right.indexes[index]) return left.indexes[index] - right.indexes[index]
    }
    return left.indexes.length - right.indexes.length
  })
  return roundRobinUnique(streams, MAXIMUM_BICYCLE_DESTINATIONS)
}

export async function exactBicycleTransitAccessPoints(
  nodes: MultimodalNode[],
  origins: LocationPoint[],
  allowedModesByOrigin: ScheduledModeSet[],
  bicycleEnabledByOrigin: boolean[],
  candidateIndexes: number[],
  loadMatrix: MatrixLoader = roadMatrix,
): Promise<StationAccess[][]> {
  const accessByOrigin: StationAccess[][] = origins.map(() => [])
  const enabledOriginIndexes = origins.flatMap((_, index) => bicycleEnabledByOrigin[index] ? [index] : [])
  if (!enabledOriginIndexes.length || !candidateIndexes.length) return accessByOrigin
  const durations = await loadMatrix(
    'bike',
    enabledOriginIndexes.map((index) => origins[index]),
    candidateIndexes.map((index) => nodeVenue(nodes[index])),
  )
  durations.forEach((row, rowIndex) => {
    const originIndex = enabledOriginIndexes[rowIndex]
    const allowedModes = new Set(allowedModesByOrigin[originIndex])
    accessByOrigin[originIndex] = row.flatMap((travelSeconds, columnIndex) => {
      const stationIndex = candidateIndexes[columnIndex]
      const node = nodes[stationIndex]
      if (travelSeconds === null || !allowedModes.has(node[4])
        || distanceKilometres(origins[originIndex], { lat: node[2], lon: node[3] }) > MAXIMUM_BICYCLE_ACCESS_KM) {
        return []
      }
      return [{
        stationIndex,
        mode: 'bike' as const,
        travelSeconds,
        seconds: travelSeconds + BICYCLE_PARKING_SECONDS + boardingSeconds(node[4]),
      }]
    })
  })
  return accessByOrigin
}

function nearestNodes(
  nodes: MultimodalNode[],
  origin: LocationPoint,
  mode: MultimodalNode[4],
  maximum: number,
): number[] {
  return nodes.flatMap((node, stationIndex) => node[4] === mode ? [{
    stationIndex,
    distance: distanceKilometres(origin, { lat: node[2], lon: node[3] }),
  }] : [])
    .filter(({ distance }) => distance <= MAXIMUM_BICYCLE_ACCESS_KM)
    .sort((left, right) => left.distance - right.distance || left.stationIndex - right.stationIndex)
    .slice(0, maximum)
    .map(({ stationIndex }) => stationIndex)
}

function roundRobinUnique(
  streams: Array<{ cursor: number; indexes: number[] }>,
  maximum: number,
): number[] {
  const selected: number[] = []
  const seen = new Set<number>()
  while (selected.length < maximum) {
    let added = false
    for (const stream of streams) {
      while (stream.cursor < stream.indexes.length && seen.has(stream.indexes[stream.cursor])) stream.cursor += 1
      const stationIndex = stream.indexes[stream.cursor++]
      if (stationIndex === undefined) continue
      seen.add(stationIndex)
      selected.push(stationIndex)
      added = true
      if (selected.length === maximum) break
    }
    if (!added) break
  }
  return selected
}

function estimatedBicycleSeconds(origin: LocationPoint, node: MultimodalNode): number {
  const distance = distanceKilometres(origin, { lat: node[2], lon: node[3] })
  return Math.max(60, Math.round((distance * 1.15 / 15) * 3600))
}

function boardingSeconds(mode: MultimodalNode[4]): number {
  if (mode === 'ferry') return FERRY_BOARDING_SECONDS
  return mode === 'bus' ? BUS_BOARDING_SECONDS : 0
}

function nodeVenue(node: MultimodalNode): Venue {
  return { id: node[0], name: node[1], type: `${node[4]}-stop`, lat: node[2], lon: node[3] }
}

function accessForMode(
  nodes: MultimodalNode[],
  origin: Pick<LocationPoint, 'lat' | 'lon'>,
  mode: MultimodalNode[4],
  maximumDistanceKm: number,
  maximumNodes: number,
  boardingSeconds: number,
): StationAccess[] {
  return nodes.flatMap((node, stationIndex) => node[4] === mode ? [{
    stationIndex,
    distanceKm: distanceKilometres(origin, { lat: node[2], lon: node[3] }),
  }] : [])
    .filter(({ distanceKm }) => distanceKm <= maximumDistanceKm)
    .sort((left, right) => left.distanceKm - right.distanceKm)
    .slice(0, maximumNodes)
    .map(({ stationIndex, distanceKm }) => ({
      stationIndex,
      seconds: Math.max(60, Math.round((distanceKm * 1.2 / 4.8) * 3600)) + boardingSeconds,
    }))
}
